import { AxiosResponse } from 'axios';

import axiosInstance from 'api/base';
import {
  normalizeLinks,
  normalizeSettlementConfig,
} from 'api/terminalsLinks/normalizers';
import {
  IDeleteTLSettlementConfigs,
  IGetTLGroupTerminalLinksResult,
  ITLSettlementConfig,
  TTLSettlementConfigData,
} from 'api/terminalsLinks/types';
import { FILTER_OPERATIONS } from 'components/Grids/DataGrid/Filters/utils/types';
import { normalizeBaseGist, normalizePageData } from 'utils/commonNormalizer';
import { normalizeSearchFiltersPayload } from 'utils/filters';
import { prepareFormData } from 'utils/form';
import { IAllPageFilters, IBaseGist, IPageType, TObject, TSearch } from 'utils/types';

import {
  normalizeSettlementCalculation,
  normalizeSettlementContract,
  normalizeSettlementContractExchangeRates,
  normalizeSettlementContractWorkbook,
  normalizeWorkbookComments,
} from './normalizers';
import {
  IAdjustmentCommentsPayload,
  IExchangeRate,
  IGetSettlementTerminalLinksPayload,
  IGroupEditSettlementConfigurationsForm,
  IRecalculateForm,
  ISettlementCalculation,
  ISettlementContract,
  ISettlementContractForm,
  ISettlementContractPayload,
  ISettlementContractWorkbook,
  IWorkbookComment,
  IWorkbookCommentsForm,
  TSettlementContractData,
} from './types';

const settlementContractsApi = {
  async getSettlementContracts(
    filters: IAllPageFilters,
  ): Promise<IPageType<ISettlementContract>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const { data } = await axiosInstance.get('settlements/contracts', {
      params: normalizedFilter,
    });

    return normalizePageData<TSettlementContractData, ISettlementContract>(
      data,
      normalizeSettlementContract,
      JSON.stringify(filters.search),
    );
  },

  async createSettlementContract(
    payload: ISettlementContractForm,
  ): Promise<AxiosResponse> {
    const normalizedPayload = prepareFormData(payload);
    const response = await axiosInstance.post('settlements/contracts', normalizedPayload);
    return response;
  },

  async updateSettlementContract({
    id,
    ...payload
  }: ISettlementContractPayload): Promise<AxiosResponse> {
    const normalizedPayload = prepareFormData(payload);
    const response = await axiosInstance.put(
      `settlements/contracts/${id}`,
      normalizedPayload,
    );
    return response;
  },

  async deleteSettlementContract(id: number | string): Promise<AxiosResponse> {
    const response = await axiosInstance.delete(`settlements/contracts/${id}`);
    return response;
  },

  async getSettlementContract(id: string): Promise<ISettlementContract> {
    const { data } = await axiosInstance.get(`settlements/contracts/${id}`);
    return normalizeSettlementContract(data);
  },

  async getSettlementConfigs(
    filters: IAllPageFilters,
  ): Promise<IPageType<ITLSettlementConfig>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const { data } = await axiosInstance.get('terminal-links/settlements', {
      params: normalizedFilter,
    });

    return normalizePageData<TTLSettlementConfigData, ITLSettlementConfig>(
      data,
      normalizeSettlementConfig,
      JSON.stringify(filters.search),
    );
  },

  async getSettlementContractsWorkbook(
    id: string,
    filters: IAllPageFilters,
  ): Promise<IPageType<ISettlementContractWorkbook>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const { data } = await axiosInstance.get(`settlements/contracts/${id}/workbook`, {
      params: normalizedFilter,
    });

    return normalizePageData<TObject, ISettlementContractWorkbook>(
      data,
      normalizeSettlementContractWorkbook,
      JSON.stringify(filters.search),
    );
  },

  async getSettlementCalculation(id: string): Promise<ISettlementCalculation> {
    const { data } = await axiosInstance.get(`settlements/contracts/${id}/calculation`);
    return normalizeSettlementCalculation(data);
  },

  async recalculateSettlement({
    id,
    startDate,
  }: IRecalculateForm): Promise<ISettlementCalculation> {
    const { data } = await axiosInstance.post(
      `settlements/contracts/${id}/calculation`,
      {},
      {
        params: {
          startDate,
        },
      },
    );

    return normalizeSettlementCalculation(data);
  },

  async getSettlementTerminalLinksList({
    contractId,
    disabledText,
    enabledText,
    merchantId,
    showWithId,
    showWithMerchant,
    showWithStateFlag,
  }: IGetSettlementTerminalLinksPayload): Promise<IGetTLGroupTerminalLinksResult> {
    const search: TSearch = {};

    if (merchantId) {
      search['merchantTerminal.merchant.id'] = {
        operation: FILTER_OPERATIONS.EQUALS,
        value: merchantId,
      };
    }

    const normalizedFilter = normalizeSearchFiltersPayload({ search });

    const response = await axiosInstance.get(
      `settlements/contracts/terminal-links/descriptions`,
      {
        params: { ...normalizedFilter, contractId },
      },
    );

    return normalizeLinks(response.data, {
      disabledText,
      enabledText,
      showWithId,
      showWithMerchant,
      showWithStateFlag,
    });
  },

  async groupEditSettlementConfigs({
    parameters,
    search,
  }: IGroupEditSettlementConfigurationsForm): Promise<AxiosResponse> {
    const normalizedFilter = normalizeSearchFiltersPayload({ search });

    const response = await axiosInstance.put(`terminal-links/settlements`, parameters, {
      params: { search: normalizedFilter.search },
    });

    return response;
  },

  async deleteSettlementConfigs(
    search: IDeleteTLSettlementConfigs,
  ): Promise<AxiosResponse> {
    const normalizedFilter = normalizeSearchFiltersPayload(search);

    const response = await axiosInstance.delete(`terminal-links/settlements`, {
      params: { search: normalizedFilter.search },
    });

    return response;
  },

  async getSettlementContractsExchangeRates(
    id: string,
    filters: IAllPageFilters,
  ): Promise<IPageType<IExchangeRate>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const { data } = await axiosInstance.get(
      `settlements/contracts/${id}/exchange-rates`,
      {
        params: normalizedFilter,
      },
    );

    return normalizePageData<TObject, IExchangeRate>(
      data,
      normalizeSettlementContractExchangeRates,
      JSON.stringify(filters.search),
    );
  },

  async createAdjustmentForm(payload: IWorkbookCommentsForm): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await axiosInstance.post(
      `settlements/contracts/workbook/adjustment`,
      normalizedPayload,
    );

    return normalizeBaseGist(response?.data);
  },

  async getAdjustmentComments(
    payload: IAdjustmentCommentsPayload,
  ): Promise<IWorkbookComment[]> {
    const { adjustmentType, calculationDate, contractId } = payload;
    const { data } = await axiosInstance.get(
      `settlements/contracts/${contractId}/workbook/adjustment`,
      { params: { date: calculationDate, type: adjustmentType } },
    );
    return normalizeWorkbookComments(data);
  },

  async deleteContractAdjustment(id: number | string): Promise<AxiosResponse> {
    const response = await axiosInstance.delete(
      `settlements/contracts/workbook/adjustment/${id}`,
    );
    return response;
  },

  async editAdjustmentForm(payload: IWorkbookCommentsForm): Promise<IBaseGist> {
    const { amount, comment, customFee, extendedDetails, feeMode, id, includeInHold } =
      payload;
    const normalizedPayload = prepareFormData({
      amount,
      comment,
      customFee,
      extendedDetails,
      feeMode,
      includeInHold,
    });

    const response = await axiosInstance.put(
      `settlements/contracts/workbook/adjustment/${id}`,
      normalizedPayload,
    );

    return normalizeBaseGist(response?.data);
  },

  async deleteContractCalculating(id: number | string): Promise<AxiosResponse> {
    const response = await axiosInstance.delete(
      `settlements/contracts/${id}/calculation`,
    );
    return response;
  },
};

export default settlementContractsApi;
